import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useLanguage } from '../../../context/LanguageContext';
import {
  pushSupported, getPermissionStatus, requestPermissionAndToken,
  getCurrentToken, onForegroundMessage, revokeToken,
} from '../../../firebase/fcmService';

/**
 * Push Notifications test page (isolated FCM feature).
 * - shows permission status
 * - requests permission + generates FCM token
 * - displays & copies the token
 * - tests a foreground notification
 * - lists received foreground messages
 */
export default function PushNotificationsPage() {
  const { isDark } = useTheme();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);

  const [supported, setSupported] = useState(null);
  const [permission, setPermission] = useState(getPermissionStatus());
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [received, setReceived] = useState([]);
  const unsubRef = useRef(null);

  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const txt = isDark ? 'text-white' : 'text-gray-900';

  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = await pushSupported();
      if (!alive) return;
      setSupported(ok);
      if (ok && getPermissionStatus() === 'granted') {
        const tk = await getCurrentToken();
        if (alive && tk) setToken(tk);
      }
    })();
    // Foreground listener
    unsubRef.current = onForegroundMessage((payload) => {
      const n = payload.notification || payload.data || {};
      setReceived((prev) => [{ title: n.title || 'Notification', body: n.body || '', at: new Date() }, ...prev].slice(0, 20));
      // Also surface a browser notification while focused.
      try { if (Notification.permission === 'granted') new Notification(n.title || 'Notification', { body: n.body || '' }); } catch (_) {}
    });
    return () => { alive = false; if (unsubRef.current) unsubRef.current(); };
  }, []);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const enable = async () => {
    setBusy(true);
    try {
      const tk = await requestPermissionAndToken();
      setToken(tk);
      setPermission(getPermissionStatus());
      notify(t('✅ Notifications activées et token enregistré.', '✅ تم تفعيل الإشعارات وحفظ الرمز.'));
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    await revokeToken();
    setToken(''); setPermission(getPermissionStatus());
    notify(t('🔕 Notifications désactivées.', '🔕 تم تعطيل الإشعارات.'));
    setBusy(false);
  };

  const copy = () => { try { navigator.clipboard.writeText(token); notify(t('📋 Token copié', '📋 تم نسخ الرمز')); } catch (_) {} };

  const testLocal = () => {
    if (Notification.permission !== 'granted') { notify(t('Activez d\'abord les notifications.', 'فعّل الإشعارات أولاً.')); return; }
    try {
      new Notification(t('🔔 Test notification', '🔔 إشعار تجريبي'), { body: t('Ceci est une notification de test locale.', 'هذا إشعار تجريبي محلي.') });
      setReceived((prev) => [{ title: t('Test notification', 'إشعار تجريبي'), body: t('Notification de test locale', 'إشعار تجريبي محلي'), at: new Date() }, ...prev]);
    } catch (e) { notify('❌ ' + e.message); }
  };

  const permBadge = () => {
    const map = {
      granted: ['bg-green-100 text-green-700', t('Autorisées', 'مسموح')],
      denied: ['bg-red-100 text-red-700', t('Refusées', 'مرفوض')],
      default: ['bg-amber-100 text-amber-700', t('Non demandées', 'لم تُطلب')],
      unsupported: ['bg-gray-100 text-gray-600', t('Non supportées', 'غير مدعوم')],
    };
    const [cls, label] = map[permission] || map.default;
    return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="mb-5">
        <h1 className={`text-2xl font-black flex items-center gap-2 ${txt}`}>🔔 {t('Notifications Push', 'الإشعارات الفورية')}</h1>
        <p className={`text-sm mt-1 ${sub}`}>{t('Firebase Cloud Messaging (FCM) — test et gestion du token.', 'Firebase Cloud Messaging — اختبار وإدارة الرمز.')}</p>
      </div>

      {supported === false && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-700 dark:text-amber-300 mb-4">
          ⚠️ {t('Push non disponible : navigateur non compatible OU configuration Firebase manquante (variables VITE_FIREBASE_*).',
                 'الإشعارات غير متاحة: المتصفح غير متوافق أو إعداد Firebase ناقص.')}
        </div>
      )}

      {/* Status card */}
      <div className={`rounded-2xl border p-4 mb-4 ${cardBg}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`font-bold ${txt}`}>{t('Statut de permission', 'حالة الإذن')}</span>
          {permBadge()}
        </div>
        <div className="flex gap-2 flex-wrap">
          {permission !== 'granted' ? (
            <button onClick={enable} disabled={busy || supported === false}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm disabled:opacity-60">
              {busy ? '⏳' : '🔔 ' + t('Activer les notifications', 'تفعيل الإشعارات')}
            </button>
          ) : (
            <>
              <button onClick={testLocal} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm">
                🧪 {t('Tester une notification', 'اختبار إشعار')}
              </button>
              <button onClick={disable} disabled={busy} className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl font-bold text-sm">
                🔕 {t('Désactiver', 'تعطيل')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Token card */}
      {token && (
        <div className={`rounded-2xl border p-4 mb-4 ${cardBg}`}>
          <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${sub}`}>{t('Token FCM de cet appareil', 'رمز FCM لهذا الجهاز')}</div>
          <textarea readOnly value={token} rows={3}
            className={`w-full text-[11px] font-mono rounded-lg border p-2 ${isDark ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`} />
          <button onClick={copy} className="mt-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-bold text-xs">📋 {t('Copier le token', 'نسخ الرمز')}</button>
        </div>
      )}

      {/* Received messages */}
      <div className={`rounded-2xl border p-4 ${cardBg}`}>
        <div className={`font-bold mb-2 ${txt}`}>📥 {t('Notifications reçues', 'الإشعارات المستلمة')}</div>
        {received.length === 0 ? (
          <div className={`text-sm text-center py-4 ${sub}`}>{t('Aucune notification pour le moment.', 'لا توجد إشعارات بعد.')}</div>
        ) : (
          <div className="space-y-2">
            {received.map((r, i) => (
              <div key={i} className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className={`font-bold text-sm ${txt}`}>{r.title}</div>
                {r.body && <div className={`text-xs ${sub}`}>{r.body}</div>}
                <div className={`text-[10px] ${sub}`}>{r.at.toLocaleTimeString(isAr ? 'ar-MA' : 'fr-FR')}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {msg && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[2100] bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg">{msg}</div>
      )}
    </div>
  );
}
